// Global Instructions Rule Applied!
//
// Period Highlights — Zod models (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md
// Phase 1): the versioned highlight sets a fund / company carries per period,
// their items with frozen metrics and resolved evidence, and the list
// filter options. Keys follow the RPCs' table__column convention.
//
// Plain string ids (never .uuid()): legacy Dataverse account/fund ids are not
// all uuid-shaped. Every response is strict-validated before it reaches UI
// state; a failure is an error, never coerced (CLAUDE.md).
import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

export const HighlightSetRecordSchema = z
  .object({
    highlight_sets__id: z.string(),
    highlight_sets__scope_type: z.string(),
    highlight_sets__account_id: z.string().nullish(),
    highlight_sets__fund_id: z.string().nullish(),
    highlight_sets__fund_group_id: z.string().nullish(),
    highlight_sets__period_start: z.string(),
    highlight_sets__period_end: z.string(),
    highlight_sets__version: z.number(),
    highlight_sets__status: z.string(),
    highlight_sets__is_current: z.boolean(),
    highlight_sets__narrative: z.string().nullish(),
    // Number tokens (2026-09-07): the set-level {token} values, the
    // narrative's edit-freeze, the in-place edit counter, and the resolved
    // narrative + token catalogue the form shows.
    highlight_sets__original_narrative: z.string().nullish(),
    highlight_sets__narrative_edited_at: z.string().nullish(),
    highlight_sets__metrics: z.record(z.string(), z.any()).nullish(),
    highlight_sets__revision: z.number(),
    highlight_sets__summary: z.string().nullish(),
    highlight_sets__params: z.any().nullish(),
    highlight_sets__source_versions: z.any().nullish(),
    highlight_sets__generated_at: z.string().nullish(),
    highlight_sets__approved_at: z.string().nullish(),
    highlight_sets__signed_off_at: z.string().nullish(),
    highlight_sets__signed_off_revision: z.number().nullish(),
    highlight_sets__rejected_at: z.string().nullish(),
    highlight_sets__reject_reason: z.string().nullish(),
    highlight_sets__date_created: z.string(),
    highlight_sets__date_modified: z.string().nullish(),
    prog__scope_id: z.string(),
    prog__scope_name: z.string().nullish(),
    prog__period_label: z.string(),
    prog__narrative_resolved: z.string().nullish(),
    prog__narrative_edited_by_name: z.string().nullish(),
    prog__metric_tokens: z.array(z.object({ key: z.string(), label: z.string(), display: z.string() }).strict()).nullish(),
    // The narrative cap this set is edited under (2026-10-28): a company
    // summary (params.layout = 'company_summary') gets the larger cfg cap.
    prog__narrative_max_chars: z.number(),
    prog__item_count: z.number(),
    prog__included_count: z.number(),
    prog__edited_count: z.number(),
    // params.coverage as the tracker wrote it (CoverageNote below); z.any()
    // because older sets carry only expected / found / missing.
    prog__coverage: z.any().nullish(),
    prog__coverage_complete: z.boolean().nullish(),
    prog__current_approved_set_id: z.string().nullish(),
    // A closed quarter's Live set once the key has a current version (backlog
    // 2.21): no longer fed or read; the list leaves it out.
    prog__live_retired: z.boolean(),
    prog__approved_by_name: z.string().nullish(),
    prog__rejected_by_name: z.string().nullish(),
    // Sign-off (2026-09-07): signed_off | edited_since_sign_off | unreviewed | null.
    prog__signed_off_by_name: z.string().nullish(),
    prog__review_status: z.string().nullish(),
    agent_runs__id: z.string().nullish(),
    agent_runs__status: z.string().nullish(),
    agent_runs__total_cost_usd: z.number().nullish(),
    agent_runs__error: z.string().nullish(),
    internal_agent_types__agent_type_key: z.string().nullish(),
    internal_agent_types__name: z.string().nullish()
  })
  .strict()
export type HighlightSetRecord = z.infer<typeof HighlightSetRecordSchema>
export const HighlightSetRecordFields = makeFieldKeys(HighlightSetRecordSchema)
export const validateHighlightSetRows = (data: unknown): HighlightSetRecord[] =>
  validateWithZodSchema(z.array(HighlightSetRecordSchema), data, 'highlight sets') as HighlightSetRecord[]

export const HighlightEvidenceSchema = z
  .object({
    highlight_item_evidence__id: z.string(),
    highlight_item_evidence__evidence_type: z.string(),
    highlight_item_evidence__evidence_id: z.string(),
    highlight_item_evidence__excerpt: z.string().nullish(),
    prog__label: z.string().nullish(),
    prog__occurred_at: z.string().nullish(),
    prog__link: z.object({ table: z.string(), id: z.string().nullish() }).nullish()
  })
  .strict()
export type HighlightEvidence = z.infer<typeof HighlightEvidenceSchema>

export const HighlightItemRecordSchema = z
  .object({
    highlight_items__id: z.string(),
    highlight_items__highlight_set_id: z.string(),
    highlight_items__sequence: z.number(),
    highlight_items__headline: z.string(),
    highlight_items__body: z.string().nullish(),
    highlight_items__short_text: z.string().nullish(),
    highlight_items__metrics: z.record(z.string(), z.any()).nullish(),
    highlight_items__materiality: z.number().nullish(),
    highlight_items__confidence: z.number().nullish(),
    highlight_items__is_positive: z.boolean().nullish(),
    highlight_items__weight: z.number().nullish(),
    highlight_items__status: z.string(),
    highlight_items__source_account_id: z.string().nullish(),
    highlight_items__source_item_id: z.string().nullish(),
    highlight_items__source_set_id: z.string().nullish(),
    highlight_items__original_headline: z.string().nullish(),
    highlight_items__original_body: z.string().nullish(),
    highlight_items__original_short_text: z.string().nullish(),
    highlight_items__edited_at: z.string().nullish(),
    highlight_items__stale_category: z.boolean(),
    prog__edited_by_name: z.string().nullish(),
    prog__is_edited: z.boolean(),
    prog__headline_resolved: z.string().nullish(),
    prog__body_resolved: z.string().nullish(),
    prog__short_text_resolved: z.string().nullish(),
    prog__metric_tokens: z.array(z.object({ key: z.string(), label: z.string(), display: z.string() }).strict()).nullish(),
    highlight_categories__id: z.string(),
    highlight_categories__category_key: z.string(),
    highlight_categories__label: z.string(),
    highlight_categories__polarity: z.string(),
    highlight_categories__deterministic: z.boolean(),
    prog__source_account_name: z.string().nullish(),
    prog__evidence: z.array(HighlightEvidenceSchema)
  })
  .strict()
export type HighlightItemRecord = z.infer<typeof HighlightItemRecordSchema>
export const HighlightItemRecordFields = makeFieldKeys(HighlightItemRecordSchema)

export const HighlightSetVersionSchema = z
  .object({
    highlight_sets__id: z.string(),
    highlight_sets__version: z.number(),
    highlight_sets__status: z.string(),
    highlight_sets__is_current: z.boolean(),
    highlight_sets__generated_at: z.string().nullish(),
    highlight_sets__approved_at: z.string().nullish()
  })
  .strict()
export type HighlightSetVersion = z.infer<typeof HighlightSetVersionSchema>

// A company summary's note on params (2026-10-28): the companies the scope
// held in the period and the threshold that chose the mode.
export const HighlightCompanySummaryNoteSchema = z
  .object({
    companies: z.array(z.object({ account_id: z.string().nullish(), name: z.string().nullish() }).passthrough()),
    count: z.number(),
    threshold: z.number()
  })
  .passthrough()
export type HighlightCompanySummaryNote = z.infer<typeof HighlightCompanySummaryNoteSchema>

export const HighlightSetDetailSchema = z
  .object({
    set: HighlightSetRecordSchema,
    items: z.array(HighlightItemRecordSchema),
    versions: z.array(HighlightSetVersionSchema),
    // The text caps plus `included` (runners-up, 2026-10-29): how many items
    // may be selected — the agent type's highlight_max_items, what the
    // reports print; pinned introductions never count.
    caps: z.object({ headline: z.number(), body: z.number(), short_text: z.number(), included: z.number() })
  })
  .strict()
export type HighlightSetDetail = z.infer<typeof HighlightSetDetailSchema>
export const validateHighlightSetDetail = (data: unknown): HighlightSetDetail =>
  validateWithZodSchema(HighlightSetDetailSchema, data, 'highlight set detail') as HighlightSetDetail

export const HighlightStalenessDeltaSchema = z
  .object({
    point: z.string(),
    key: z.string(),
    frozen: z.number().nullish(),
    live: z.number().nullish(),
    delta_pct: z.number().nullish()
  })
  .strict()
// Item-aware (2026-09-07): per deterministic item, the metric keys that
// moved and whether the books still produce an item of that category.
export const HighlightStalenessItemSchema = z
  .object({
    item_id: z.string(),
    sequence: z.number().nullish(),
    category_key: z.string().nullish(),
    stale_category: z.boolean(),
    metrics_live: z.record(z.string(), z.any()).nullish(),
    deltas: z.array(HighlightStalenessDeltaSchema)
  })
  .strict()
// New in the books (2026-10-31): a story the books tell now that the set
// does not hold, because the company's activity in the period changed after
// the set was generated (a valuation booked late or re-dated into the
// quarter). headline is resolved; activity_then / activity_now are the
// company's transaction types in the period then and now.
export const HighlightNewStorySchema = z
  .object({
    source_account_id: z.string(),
    source_account_name: z.string().nullish(),
    category_key: z.string(),
    headline: z.string(),
    is_positive: z.boolean(),
    pinned: z.boolean(),
    materiality: z.number().nullish(),
    activity_then: z.array(z.string()),
    activity_now: z.array(z.string())
  })
  .strict()
export type HighlightNewStory = z.infer<typeof HighlightNewStorySchema>
export const HighlightSetStalenessSchema = z
  .object({
    highlight_sets__id: z.string(),
    highlight_sets__version: z.number().nullish(),
    highlight_sets__generated_at: z.string().nullish(),
    stale: z.boolean(),
    compared: z.number(),
    tolerance_pct: z.number(),
    checked_at: z.string(),
    deltas: z.array(HighlightStalenessDeltaSchema),
    items: z.array(HighlightStalenessItemSchema).optional().default([]),
    new_stories: z.array(HighlightNewStorySchema).optional().default([]),
    set_metrics_live: z.record(z.string(), z.any()).nullish()
  })
  .strict()
export type HighlightSetStaleness = z.infer<typeof HighlightSetStalenessSchema>
export const validateHighlightSetStaleness = (data: unknown): HighlightSetStaleness =>
  validateWithZodSchema(HighlightSetStalenessSchema, data, 'highlight set staleness') as HighlightSetStaleness

// Refresh numbers (staleness.refresh_set_metrics): the write-back's counts
// and the check it ran. revision_bumped (backlog 2.24) = a figure the set
// prints moved beyond the tolerance, or an included item lost its category:
// a signed-off version then reads "edited since sign-off".
export const HighlightRefreshMetricsResultSchema = z
  .object({
    written: z
      .object({
        highlight_sets__id: z.string(),
        items_updated: z.number(),
        items_stale_category: z.number(),
        metrics_refreshed_at: z.string(),
        figures_moved: z.number(),
        items_newly_stale: z.number(),
        revision_bumped: z.boolean(),
        prog__review_status: z.string().nullish()
      })
      .strict(),
    check: HighlightSetStalenessSchema
  })
  .strict()
export type HighlightRefreshMetricsResult = z.infer<typeof HighlightRefreshMetricsResultSchema>
export const validateHighlightRefreshMetricsResult = (data: unknown): HighlightRefreshMetricsResult =>
  validateWithZodSchema(HighlightRefreshMetricsResultSchema, data, 'highlight refresh result') as HighlightRefreshMetricsResult

// Restore as new version (highlight_set__clone_draft, backlog 2.23).
export const HighlightCloneDraftResultSchema = z
  .object({
    highlight_sets__id: z.string(),
    highlight_sets__version: z.number(),
    highlight_sets__status: z.string(),
    base_set_id: z.string()
  })
  .strict()
export type HighlightCloneDraftResult = z.infer<typeof HighlightCloneDraftResultSchema>
export const validateHighlightCloneDraftResult = (data: unknown): HighlightCloneDraftResult =>
  validateWithZodSchema(HighlightCloneDraftResultSchema, data, 'highlight draft') as HighlightCloneDraftResult

// Notes the sign-off lifecycle leaves in highlight_sets.params (read by the
// form's review tooltips): the last sign-off withdrawn (2.22) and the last
// Refresh numbers (2.24). params is free-form jsonb, so other keys pass.
export const HighlightSignOffWithdrawalSchema = z
  .object({
    withdrawn_by_name: z.string().nullish(),
    withdrawn_at: z.string().nullish(),
    signed_off_by_name: z.string().nullish(),
    signed_off_at: z.string().nullish()
  })
  .passthrough()
// Narrative regeneration (highlight_set__regenerate_narrative, backlog
// 2.21): the last pass, on params.narrative_regenerated.
export const HighlightNarrativeRegenNoteSchema = z
  .object({
    at: z.string().nullish(),
    run_id: z.string().nullish(),
    changed: z.boolean().nullish(),
    summary: z.string().nullish(),
    selection: z.array(z.string()).nullish()
  })
  .passthrough()
export type HighlightNarrativeRegenNote = z.infer<typeof HighlightNarrativeRegenNoteSchema>
export const HighlightMetricsRefreshNoteSchema = z
  .object({
    at: z.string().nullish(),
    figures_moved: z.number().nullish(),
    items_newly_stale: z.number().nullish(),
    revision_bumped: z.boolean().nullish()
  })
  .passthrough()

// Withdraw sign-off (highlight_set__withdraw_sign_off, backlog 2.22).
export const HighlightWithdrawSignOffResultSchema = z
  .object({
    highlight_sets__id: z.string(),
    prog__review_status: z.string().nullish()
  })
  .strict()
export type HighlightWithdrawSignOffResult = z.infer<typeof HighlightWithdrawSignOffResultSchema>
export const validateHighlightWithdrawSignOffResult = (data: unknown): HighlightWithdrawSignOffResult =>
  validateWithZodSchema(HighlightWithdrawSignOffResultSchema, data, 'highlight sign-off withdrawal') as HighlightWithdrawSignOffResult

// Generate-history manifest (Phase 5): the dry-run preview and the enqueue
// result share one shape; results/targets are capped server-side.
const HistoryPeriodSchema = z.object({ period_start: z.string(), period_end: z.string() }).strict()
// A current version (or open draft) a forced backfill will supersede that a
// person signed off or edited by hand (backlog 2.25).
const HistoryReplacedSchema = z
  .object({
    scope_type: z.string(),
    scope_id: z.string(),
    name: z.string().nullish(),
    instance_id: z.string(),
    period_start: z.string(),
    period_end: z.string(),
    set_id: z.string(),
    version: z.number(),
    status: z.string(),
    review_status: z.string().nullish(),
    items_edited: z.number(),
    narrative_edited: z.boolean()
  })
  .strict()
const HistoryTargetSchema = z
  .object({
    scope_type: z.string(),
    scope_id: z.string(),
    name: z.string().nullish(),
    instance_id: z.string(),
    period_start: z.string(),
    period_end: z.string(),
    reason: z.string().optional(),
    status: z.string().optional(),
    run_id: z.string().nullish(),
    error: z.string().optional()
  })
  .strict()
// One subject a backfill preview was narrowed to: its name and how many
// provisioned tracker instances it has (0 → provision it first).
const HistorySubjectSchema = z
  .object({ scope_type: z.string(), id: z.string(), name: z.string().nullish(), instances: z.number() })
  .strict()
export type HighlightHistorySubject = z.infer<typeof HistorySubjectSchema>

export const HighlightHistoryManifestSchema = z
  .object({
    quarters: z.number(),
    force: z.boolean(),
    // Under force: leave the keys whose version was signed off or hand-edited.
    keep_reviewed: z.boolean(),
    dry_run: z.boolean(),
    stagger_minutes: z.number(),
    // The funds / consolidated funds / LPs the request named (the Follower
    // Agents → Backfill form's multi-select, 2026-10-29), in request order,
    // each with its provisioned instance count; null for the fleet.
    subjects: z.array(HistorySubjectSchema).nullish(),
    scopes: z.array(
      z.object({
        scope_type: z.string(),
        agent_type_key: z.string(),
        instances: z.number(),
        lag_days: z.number(),
        periods: z.array(HistoryPeriodSchema),
        sets_to_generate: z.number(),
        sets_skipped: z.number()
      }).strict()
    ),
    periods: z.array(HistoryPeriodSchema),
    atoms: z.array(
      z.object({
        period_start: z.string(),
        period_end: z.string(),
        companies: z.number(),
        atoms_missing: z.number(),
        atoms_found: z.number(),
        // Members with only a live set: frozen into atoms at no cost (0 under force).
        atoms_live: z.number()
      }).strict()
    ),
    sets_to_generate: z.number(),
    sets_skipped: z.number(),
    atoms_missing: z.number(),
    estimate: z
      .object({
        atom_cost_usd: z.number(),
        set_cost_usd: z.number(),
        atoms_usd: z.number(),
        sets_usd: z.number(),
        total_usd: z.number()
      })
      .strict(),
    targets: z.array(HistoryTargetSchema),
    skipped: z.array(HistoryTargetSchema),
    skipped_truncated: z.boolean(),
    replaces: z
      .object({
        versions: z.number(),
        signed_off: z.number(),
        edited: z.number(),
        rows: z.array(HistoryReplacedSchema),
        truncated: z.boolean()
      })
      .strict(),
    // enqueued = accepted (new + merged + deferred). The split says what
    // actually happened: an instance that already holds a queued row merges
    // (same period) or defers (parks the period) and no new run appears.
    enqueued: z.number(),
    enqueued_new: z.number().nullish(),
    merged: z.number().nullish(),
    deferred: z.number().nullish(),
    enqueue_errors: z.number().nullish(),
    results: z.array(HistoryTargetSchema)
  })
  .strict()
export type HighlightHistoryManifest = z.infer<typeof HighlightHistoryManifestSchema>
export const validateHighlightHistoryManifest = (data: unknown): HighlightHistoryManifest =>
  validateWithZodSchema(HighlightHistoryManifestSchema, data, 'highlight history manifest') as HighlightHistoryManifest

// The runners-up backfill manifest (2026-10-29): the same preview-then-commit
// idiom, model-free — one stage 'bench' run per (instance, quarter) whose key
// has a current approved set.
const BenchTargetSchema = HistoryTargetSchema.extend({ set_id: z.string().optional(), version: z.number().optional() })
export const HighlightBenchManifestSchema = z
  .object({
    mode: z.literal('bench'),
    quarters: z.number(),
    bench_items: z.number(),
    dry_run: z.boolean(),
    stagger_minutes: z.number(),
    subjects: z.array(HistorySubjectSchema).nullish(),
    scopes: z.array(
      z.object({
        scope_type: z.string(),
        agent_type_key: z.string(),
        instances: z.number(),
        lag_days: z.number(),
        periods: z.array(HistoryPeriodSchema),
        sets_to_update: z.number(),
        sets_skipped: z.number()
      }).strict()
    ),
    periods: z.array(HistoryPeriodSchema),
    sets_to_update: z.number(),
    sets_skipped: z.number(),
    targets: z.array(BenchTargetSchema),
    skipped: z.array(BenchTargetSchema),
    skipped_truncated: z.boolean(),
    enqueued: z.number(),
    enqueued_new: z.number().nullish(),
    merged: z.number().nullish(),
    deferred: z.number().nullish(),
    enqueue_errors: z.number().nullish(),
    results: z.array(BenchTargetSchema)
  })
  .strict()
export type HighlightBenchManifest = z.infer<typeof HighlightBenchManifestSchema>
export const validateHighlightBenchManifest = (data: unknown): HighlightBenchManifest =>
  validateWithZodSchema(HighlightBenchManifestSchema, data, 'highlight runners-up manifest') as HighlightBenchManifest

// The agent's rewording of stored highlight texts (owner 2026-11-05): the
// same preview-then-commit idiom — one stage 'reword' run (one model call)
// per current set whose selected items break the house rules (a text cut
// off with "…", an investment introduction restating its heading).
const RewordingTargetSchema = z
  .object({
    scope_type: z.string(),
    scope_id: z.string(),
    name: z.string().nullish(),
    instance_id: z.string().nullish(),
    period_start: z.string(),
    period_end: z.string(),
    period_label: z.string().nullish(),
    set_id: z.string(),
    version: z.number().nullish(),
    set_status: z.string().nullish(),
    items: z.number(),
    item_ids: z.array(z.string()),
    reasons: z.array(z.string()),
    status: z.string().optional(),
    run_id: z.string().nullish(),
    error: z.string().optional()
  })
  .strict()
export type HighlightRewordingTarget = z.infer<typeof RewordingTargetSchema>
export const HighlightRewordingManifestSchema = z
  .object({
    mode: z.literal('reword'),
    dry_run: z.boolean(),
    stagger_minutes: z.number(),
    subjects: z.array(HistorySubjectSchema).nullish(),
    scopes: z.array(
      z
        .object({
          scope_type: z.string(),
          agent_type_key: z.string(),
          sets_seen: z.number(),
          sets_to_update: z.number(),
          items_to_reword: z.number()
        })
        .strict()
    ),
    sets_seen: z.number(),
    sets_to_update: z.number(),
    items_seen: z.number(),
    items_edited: z.number(),
    items_to_reword: z.number(),
    reasons: z.array(z.object({ reason: z.string(), items: z.number() }).strict()),
    targets: z.array(RewordingTargetSchema),
    targets_truncated: z.boolean(),
    enqueued: z.number(),
    enqueued_new: z.number().nullish(),
    merged: z.number().nullish(),
    deferred: z.number().nullish(),
    enqueue_errors: z.number().nullish(),
    results: z.array(RewordingTargetSchema)
  })
  .strict()
export type HighlightRewordingManifest = z.infer<typeof HighlightRewordingManifestSchema>
export const validateHighlightRewordingManifest = (data: unknown): HighlightRewordingManifest =>
  validateWithZodSchema(HighlightRewordingManifestSchema, data, 'highlight rewording manifest') as HighlightRewordingManifest

// highlight_rewording__items: the Follower Agents review list. prog__status
// is the rewording's own review (unreviewed | signed_off);
// prog__set_review_status the set's (signed_off | edited_since_sign_off |
// unreviewed, null on a draft).
export const RewordedHighlightItemSchema = z
  .object({
    highlight_items__id: z.string(),
    highlight_sets__id: z.string(),
    highlight_sets__scope_type: z.string(),
    prog__scope_id: z.string(),
    prog__scope_name: z.string().nullable(),
    highlight_sets__period_start: z.string(),
    highlight_sets__period_end: z.string(),
    prog__period_label: z.string().nullable(),
    highlight_sets__status: z.string(),
    highlight_sets__version: z.number(),
    prog__set_review_status: z.string().nullable(),
    highlight_categories__category_key: z.string(),
    highlight_categories__label: z.string().nullable(),
    prog__source_account_name: z.string().nullable(),
    prog__pinned: z.boolean(),
    highlight_items__status: z.string(),
    highlight_items__headline: z.string(),
    highlight_items__body: z.string().nullable(),
    highlight_items__short_text: z.string().nullable(),
    prog__headline_resolved: z.string().nullable(),
    prog__body_resolved: z.string().nullable(),
    prog__short_text_resolved: z.string().nullable(),
    prog__before_headline_resolved: z.string().nullable(),
    prog__before_body_resolved: z.string().nullable(),
    prog__before_short_text_resolved: z.string().nullable(),
    highlight_items__reworded_at: z.string(),
    highlight_items__reworded_reviewed_at: z.string().nullable(),
    prog__reviewed_by_name: z.string().nullable(),
    prog__status: z.enum(['unreviewed', 'signed_off'])
  })
  .strict()
export type RewordedHighlightItem = z.infer<typeof RewordedHighlightItemSchema>
export const RewordedHighlightItemsSchema = z
  .object({
    caps: z.object({ headline: z.number(), body: z.number(), short_text: z.number() }).strict(),
    items: z.array(RewordedHighlightItemSchema)
  })
  .strict()
export type RewordedHighlightItems = z.infer<typeof RewordedHighlightItemsSchema>
export const validateRewordedHighlightItems = (data: unknown): RewordedHighlightItems =>
  validateWithZodSchema(RewordedHighlightItemsSchema, data, 'reworded highlights') as RewordedHighlightItems

export const RewordingSignOffResultSchema = z.object({ signed_off: z.number(), sets_renewed: z.number() }).strict()
export type RewordingSignOffResult = z.infer<typeof RewordingSignOffResultSchema>
export const validateRewordingSignOffResult = (data: unknown): RewordingSignOffResult =>
  validateWithZodSchema(RewordingSignOffResultSchema, data, 'rewording sign-off') as RewordingSignOffResult

export const RewordingRegenerateResultSchema = z
  .object({
    enqueued: z.number(),
    results: z.array(
      z
        .object({
          set_id: z.string(),
          scope_type: z.string(),
          scope_id: z.string(),
          name: z.string().nullish(),
          period_start: z.string(),
          period_end: z.string(),
          period_label: z.string().nullish(),
          item_ids: z.array(z.string()),
          items: z.number(),
          status: z.string(),
          run_id: z.string().nullish(),
          error: z.string().optional()
        })
        .strict()
    )
  })
  .strict()
export type RewordingRegenerateResult = z.infer<typeof RewordingRegenerateResultSchema>
export const validateRewordingRegenerateResult = (data: unknown): RewordingRegenerateResult =>
  validateWithZodSchema(RewordingRegenerateResultSchema, data, 'rewording regenerate result') as RewordingRegenerateResult

// What a rendered report says about the highlights it carries (the
// X-Document-Report-Highlights header): whether a human reviewed and signed
// them off, and where to do so.
export const ReportHighlightsStatusSchema = z
  .object({
    status: z.enum(['signed_off', 'edited_since_sign_off', 'unreviewed', 'none']),
    set_id: z.string().nullish(),
    version: z.number().nullish(),
    scope_type: z.string().nullish(),
    scope_name: z.string().nullish(),
    period_label: z.string().nullish(),
    signed_off_by: z.string().nullish(),
    signed_off_at: z.string().nullish()
  })
  .strict()
export type ReportHighlightsStatus = z.infer<typeof ReportHighlightsStatusSchema>
export const validateReportHighlightsStatus = (data: unknown): ReportHighlightsStatus =>
  validateWithZodSchema(ReportHighlightsStatusSchema, data, 'report highlights status') as ReportHighlightsStatus

const OptionSchema = z.object({ value: z.string(), label: z.string() })
export const HighlightSetFilterOptionsSchema = z
  .object({
    highlight_sets__scope_type: z.array(OptionSchema).optional().default([]),
    highlight_sets__status: z.array(OptionSchema).optional().default([]),
    highlight_sets__fund_id: z.array(OptionSchema).optional().default([]),
    highlight_sets__account_id: z.array(OptionSchema).optional().default([]),
    highlight_sets__period_end: z.array(OptionSchema).optional().default([])
  })
  .strict()
export type HighlightSetFilterOptions = z.infer<typeof HighlightSetFilterOptionsSchema>
export const validateHighlightSetFilterOptions = (data: unknown): HighlightSetFilterOptions =>
  validateWithZodSchema(HighlightSetFilterOptionsSchema, data, 'highlight set filter options') as HighlightSetFilterOptions

/** A portfolio company a fund / LP set went ahead without (runs._left_out,
 *  2026-09-09): `paused` — the follower is paused (the company exited or
 *  died) and was not asked; `skipped` — the atom run was skipped; `failed` —
 *  the atom run failed atom_retry_max times; `draft` — the atom is a draft
 *  awaiting approval (set_id opens it); `no_atom` — the follower finished
 *  without writing one. */
export interface CoverageLeftOut {
  account_id: string
  name?: string | null
  kind: 'paused' | 'skipped' | 'failed' | 'draft' | 'no_atom'
  reason?: string | null
  run_id?: string | null
  set_id?: string | null
}

export interface CoverageNote {
  expected?: number | null
  found?: number | null
  missing?: string[]
  /** Given up on — see CoverageLeftOut. */
  unavailable?: CoverageLeftOut[]
  /** Still generating when member_wait_hours ran out. */
  wait_expired?: Array<{ account_id: string; name?: string | null }>
  live?: number
  frozen?: unknown[]
}
